import React, { memo, useCallback, useEffect, useMemo } from 'react';

import { Box } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { onAutocompleteChangeInForm } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';

import { pagesPropTypes } from '../../MainContent.constants';

import { getAlternateNameConfig } from './AlternateName.config';
import { DEBOUNCE_DELAY_MS } from './AlternateName.constants';
import { wrapperStyles } from './AlternateName.styles';

const AlternateName = ({
  changedFields,
  isLoading,
  isSaving,
  form,
  validationErrors,
  validateField,
  handleChange,
  entityId,
  section,
  setEntityConfig,
  setInForm,
  className,
  setForm,
  entityData: applicationEntityData = {},
  setValidationErrors,
  pendingChanges,
  disabledPendingChangesHighlight,
  formDataRef,
  staticValidateField,
}) => {
  const isFormDataEmpty = _.isEmpty(form[entityId]?.alternateNames);
  const entityData = isFormDataEmpty ? applicationEntityData : form[entityId];
  const isDataEmpty = _.isEmpty(entityData);

  const onAlternateNameAdd = useCallback(() => {
    setForm(prev => ({
      ...prev,
      [entityId]: {
        ...prev[entityId],
        alternateNames: [
          ...(prev[entityId]?.alternateNames || []),
          { firstName: '', lastName: '', middleName: '', suffix: null },
        ],
      },
    }));
  }, [setForm, entityId]);

  const onAlternateNameDelete = useCallback(
    deleteIndex => () => {
      const omitRowFromForm = form => ({
        ...form,
        [entityId]: {
          ...form[entityId],
          alternateNames: form[entityId]?.alternateNames?.filter(
            (_, index) => index !== deleteIndex,
          ),
        },
      });

      setValidationErrors(prev => omitRowFromForm(prev));
      setForm(prev => omitRowFromForm(prev));
    },
    [setForm, setValidationErrors, entityId],
  );

  const validateDependentFields = useCallback(
    dependentFieldsNames => {
      dependentFieldsNames.forEach(dependentFieldName => {
        const dependentFieldValue = _.get(formDataRef.current, dependentFieldName);

        setTimeout(
          () => staticValidateField(dependentFieldName, dependentFieldValue),
          DEBOUNCE_DELAY_MS,
        );
      });
    },
    [formDataRef, staticValidateField],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onAlternateNameFieldChange = useCallback(
    (handleChangeProps, dependentFieldsNames, isAutocomplete = false) => {
      if (isAutocomplete) {
        onAutocompleteChange(...handleChangeProps);
      }

      if (!isAutocomplete) {
        handleChange(...handleChangeProps);
      }

      validateDependentFields(dependentFieldsNames);
    },
    [handleChange, onAutocompleteChange, validateDependentFields],
  );

  const config = useMemo(() => {
    if (isDataEmpty) {
      return;
    }

    const alternateNamesCount = entityData?.alternateNames?.length;

    return getAlternateNameConfig({
      entityId,
      section,
      alternateNamesCount,
      onAlternateNameDelete,
      onAlternateNameAdd,
      onAlternateNameFieldChange,
    });
  }, [
    entityId,
    section,
    entityData?.alternateNames?.length,
    onAlternateNameDelete,
    onAlternateNameAdd,
    onAlternateNameFieldChange,
    isDataEmpty,
  ]);

  useEffect(() => {
    if (!config) {
      return;
    }

    setEntityConfig(config);
  }, [config, setEntityConfig]);

  if (!config) {
    return null;
  }

  return (
    <Box className={className} sx={wrapperStyles}>
      <FormGenerator
        isNestedForm
        changedFields={changedFields}
        config={config}
        disabledPendingChangesHighlight={disabledPendingChangesHighlight}
        form={form}
        formName={FormNames.ALTERNATE_NAMES}
        handleChange={handleChange}
        isLoading={isSaving || isLoading}
        numberOfColumns={1}
        pendingChanges={pendingChanges}
        setInForm={setInForm}
        validateField={validateField}
        validationErrors={validationErrors}
      />
    </Box>
  );
};

AlternateName.propTypes = pagesPropTypes;

export default memo(AlternateName);
